import React, { useEffect, useRef } from "react";
import Button from "../../component/ui/Button";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import "../../styles/ServicesSection.css";
import { Link } from "react-router-dom";

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const ServicesSection = () => {
  const cardsRef = useRef(null);
  const card1Ref = useRef(null);
  const card2Ref = useRef(null);
  // Ref to hold the ScrollTrigger instance for cleanup
  const scrollTriggerRef = useRef(null);

  useEffect(() => {
    // Check if refs are mounted
    if (!cardsRef.current || !card1Ref.current || !card2Ref.current) return;

    // Cleanup function for existing ScrollTriggers before creating new ones
    const cleanup = () => {
      if (scrollTriggerRef.current) {
        scrollTriggerRef.current.kill();
        scrollTriggerRef.current = null;
      }
      // Kill all existing triggers created within this context
      gsap.context(() => {}, cardsRef).revert();
    };
    cleanup();

    // Define the breakpoint for mobile (768px - Tailwind md/lg border)
    const isMobile = window.innerWidth < 768;

    // Use gsap.context for better cleanup of all animations created inside it
    const context = gsap.context(() => {
      // ---------------------------------------------------------------------
      // 1. Text Reveal Animation (Applicable on both mobile and desktop)
      // ---------------------------------------------------------------------
      const textElements = gsap.utils.toArray(
        cardsRef.current.querySelectorAll(".animated-service-text"),
      );

      textElements.forEach((element, index) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        // The trigger is the element itself for a better scroll experience
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element, // Trigger on the text element itself
            start: "top 85%", // Animation starts when element top hits 85% from top of viewport
            end: "bottom 70%", // Animation ends when element bottom hits 70% from top of viewport
            scrub: 1, // Smoothly link to scroll
            // markers: true,
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        tl.to(element, {
          y: 0, // Final state: Original position
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out",
          // Stagger effect removed since it's applied per element, but you can add a small delay here if needed
          // delay: index * 0.05,
        });
      });

      // ---------------------------------------------------------------------
      // 2. Card Stacking/Pinning Animation (Only for non-mobile)
      // ---------------------------------------------------------------------
      if (!isMobile) {
        // **Non-Mobile GSAP Setup**
        const pinDuration = "+=100";

        let tl = gsap.timeline({
          scrollTrigger: {
            trigger: cardsRef.current,
            pin: true,
            pinSpacing: true,
            start: "top",
            end: pinDuration,
            scrub: 1,
            markers: false,
            onRefresh: (self) => (scrollTriggerRef.current = self),
            onUpdate: (self) => (scrollTriggerRef.current = self),
          },
        });

        // --- Animation Steps ---

        tl.addLabel("card1_start");

        // Animate Card 2 In
        tl.fromTo(
          card2Ref.current,
          {
            yPercent: 75,
            opacity: 0,
          },
          {
            yPercent: 0,
            opacity: 1,
            zIndex: 20, // Finish higher than Card 1
            duration: 1,
          },
          "card1_start+=0.1",
        );

        // Scale down Card 1 while Card 2 is coming in/in place
        tl.addLabel("card2_in");
        tl.to(
          card1Ref.current,
          {
            scale: 0.925,
            yPercent: -1.5,
            opacity: 1,
            zIndex: 1, // Move Card 1 to back
            duration: 0.5,
          },
          "card2_in-=0.5",
        );

        tl.addLabel("section_end", "+=0.5");
      } else {
        // **Mobile Fallback:** Ensure all GSAP transforms are cleared
        gsap.set(card1Ref.current, { clearProps: "all" });
        gsap.set(card2Ref.current, { clearProps: "all" });
      }
    }, cardsRef);

    // Context cleanup includes all ScrollTriggers and animations created inside it
    return () => context.revert();
  }, []); // Empty dependency array means this runs once on mount

  const sectionRef = useRef(null);

  useEffect(() => {
    const context = gsap.context(() => {
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".animated-text-content"),
      );

      // --- 1. Scrubbed Text Reveal Animation (Merise.ae style) ---
      textElements.forEach((element, index) => {
        // Set Initial State: Hidden (opacity: 0), slightly down (y: 25), and clipped from the bottom
        gsap.set(element, {
          y: 25,
          opacity: 0,
          clipPath: "inset(100% 0 0% 0)",
        });

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: element.closest("div"),
            start: "top 85%", // Animation shuru karo jab element screen mein aana shuru ho
            end: "center 60%", // Animation end karo jab element screen mein lagbhag center ho
            scrub: 1, // Scroll se smooth link (scrubbing)
            // markers: true,
          },
        });

        // Define the Target State: Visible (opacity: 1), original position (y: 0), and fully unclipped
        // Scroll down karne par (timeline forward), text 25px se 0px par aayega (Neeche se Upar).
        tl.to(element, {
          y: 0, // Final state: Original position
          opacity: 1,
          clipPath: "inset(0% 0 0% 0)", // Fully visible
          duration: 1,
          ease: "power2.out", // Thoda smooth movement
          delay: index * 0.1, // Stagger effect for line-by-line reveal
        });
      });

      // --- 2. Scrubbed Divider Line Animation (Grow from Left) ---
      const lineElement = sectionRef.current.querySelector(
        ".about-divider-line",
      );
      if (lineElement) {
        // Initial State: Hidden (scaleX: 0) and set origin to the left
        gsap.set(lineElement, { scaleX: 0, transformOrigin: "left" });

        gsap.to(lineElement, {
          scaleX: 1, // Target state is visible (full width)
          duration: 1,
          ease: "none",
          scrollTrigger: {
            trigger: lineElement.closest(".about-subtitle"),
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      }
    }, sectionRef);

    return () => context.revert();
  }, []);

  return (
    <>
      <section
        ref={sectionRef}
        className="w-full max-w-[1620px] mx-auto flex flex-col gap-[25px] sm:gap-[37px] 2xl:gap-[50px] justify-start items-center mt-[48px] sm:mt-[72px] 2xl:mt-[96px] px-[20px] sm:px-6 lg:px-8 mb-[20px]"
      >
        <div className="flex flex-col gap-[11px] sm:gap-[16px] 2xl:gap-[22px] justify-start items-start w-full">
          {/* 1. Subtitle */}
          <div className="flex flex-row justify-start items-center w-full about-subtitle overflow-hidden">
            <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[20px] sm:leading-[25px] 2xl:leading-[31px] text-left text-[#1c233a] font-[Sora]  animated-text-content">
              About Us
            </p>

            <div className="w-[100px] sm:w-[150px] 2xl:w-[200px] h-[1px] sm:h-[1.5px] 2xl:h-[2px] bg-[#1c233a] ml-[15px] sm:ml-[22px] 2xl:ml-[30px] mb-[7px] sm:mb-[10px] 2xl:mb-[14px] self-end about-divider-line"></div>
          </div>
          {/* 2. Title Line 1 */}
          <div className="about-title-1 overflow-hidden mb-[0px] md:mb-[20px]">
            <h2 className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-bold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora] animated-text-content">
              Pioneering Sustainability
              <br />
              in Agriculture & Energy
            </h2>
          </div>
          {/* 3. Title Line 2 */}
          {/* <div className="about-title-2 overflow-hidden">
            <h2 className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-bold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-[#1c233a] font-[Sora]  animated-text-content">
              in Agriculture & Energy
            </h2>
          </div> */}
          {/* 4. Paragraph */}
        </div>
      </section>
      <section
        className="w-full max-w-[1620px] m-auto px-[20px] sm:px-6 lg:px-8mb-[0px] md:mb-[100px] relative"
        ref={cardsRef}
      >
        <div className="about-paragraph overflow-hidden mb-[30px] md:mb-[50px] ">
          <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] text-justify font-normal leading-[24px] sm:leading-[30px] 2xl:leading-[38px] text-left text-[#1c233a] font-[Sora] w-[100%] md:w-[74%]  animated-service-text">
            At Govardhannathji Energies LLP, we redefine sustainability in
            agriculture and energy with smart, nature-driven solutions.Through
            biogas production and organic fertilizers, we convert waste into
            renewable power and enriched soil.Our work reduces carbon
            footprints, boosts farm productivity, and supports a cleaner
            environment. We are committed to driving positive change and shaping
            a greener tomorrow.
          </p>
        </div>
        <div className="cards relative flex flex-col justify-center items-center min-h-full lg:min-h-[80vh] xl:min-h-[70vh] 2xl:mb-[90px]">
          {/* Organic Fertilizers Section - Card 1 */}
          <div
            ref={card1Ref}
            className="custom-card card1 absolute top-0 lg:relative lg:top-auto 
                     bg-[#1c233a] text-white flex flex-col-reverse lg:flex-row justify-between items-start 
                     rounded-[20px] 2xl:rounded-[50px] shadow-lg gap-[20px] sm:gap-0 
                     w-full lg:mb-0" // Add margin-bottom for mobile spacing
            style={{ zIndex: 2 }}
          >
            <div className="flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-start w-full lg:w-[54%] p-[20px] sm:p-[35px] 2xl:p-[40px] ">
              {/* Title - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <h3 className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-bold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-white font-[Sora] ">
                  Organic Fertilizers
                </h3>
              </div>
              {/* Paragraph - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[26px] sm:leading-[35px] 2xl:leading-[44px] text-justify text-white font-[Sora] w-[100%] ">
                  At Govardhanathji Energies LLP, our organic fertilizers enrich
                  soil naturally and enhance crop productivity. Formulated with
                  essential nutrients, beneficial microbes, and organic matter,
                  they improve soil health, strengthen roots, and support
                  sustainable farming. By choosing our solutions, farmers can
                  grow healthier crops while reducing chemical dependency and
                  promoting an eco-friendly environment.
                </p>
              </div>
              {/* Button - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <Link
                  to="https://utpann.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    text="Learn More"
                    text_font_size="16"
                    text_font_family="Sora"
                    text_font_weight="700"
                    text_line_height="20px"
                    text_text_align="center"
                    text_color="#ffffff"
                    border_border="1px solid #ffffff"
                    border_border_radius="30px"
                    padding="6px 17px"
                    className="mt-[20px] w-auto text-[16px] sm:text-[20px] 2xl:text-[24px] leading-[20px] sm:leading-[25px] 2xl:leading-[31px] py-[6px] px-[17px] sm:py-[9px] sm:px-[25px] 2xl:py-[12px] 2xl:px-[34px] "
                  />
                </Link>
              </div>
            </div>

            {/* Image Container */}
            <div className="image-container-fix w-full lg:w-[42%] ">
              <img
                src="/images/Top.png"
                alt="Organic Fertilizers Background"
                className="rounded-[20px] md:rounded-[30px]"
              />
            </div>
          </div>

          {/* Bio Gas Energy Section - Card 2 */}
          <div
            ref={card2Ref}
            className="custom-card card2 absolute top-[30px] lg:relative lg:top-auto mt-[30px] lg:mt-0 
                     bg-[#1c233a] text-white flex flex-col-reverse lg:flex-row justify-between items-start 
                     rounded-[50px] shadow-lg gap-[20px] w-full"
            style={{ zIndex: 3 }}
          >
            <div className="flex flex-col gap-[12px] sm:gap-[18px] 2xl:gap-[24px] justify-start items-start w-full lg:w-[54%] p-[20px] sm:p-[35px] 2xl:p-[40px] ">
              {/* Title - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <h3 className="text-[30px] sm:text-[45px] 2xl:text-[60px] font-bold leading-[38px] sm:leading-[57px] 2xl:leading-[76px] text-left text-white font-[Sora]">
                  Bio Gas Energy
                </h3>
              </div>
              {/* Paragraph - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <p className="text-[16px] sm:text-[20px] 2xl:text-[24px] font-normal leading-[26px] sm:leading-[35px] 2xl:leading-[44px] text-justify text-white font-[Sora] w-[100%]">
                  Our bio gas energy solutions efficiently convert organic waste
                  into clean, reliable, and renewable energy using advanced
                  technologies like anaerobic digestion. This sustainable
                  process drastically reduces harmful greenhouse gas emissions
                  and minimizes landfill waste, generating eco-friendly power
                  for homes, industries, and transportation. We provide robust
                  energy solutions that contribute directly to a cleaner,
                  greener, and more sustainable energy future for all.
                </p>
              </div>
              {/* Button - Added animated-service-text class and overflow-hidden parent */}
              <div className="overflow-hidden">
                <Link
                  to="https://utpann.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    text="Learn More"
                    text_font_size="16"
                    text_font_family="Sora"
                    text_font_weight="700"
                    text_line_height="20px"
                    text_text_align="center"
                    text_color="#ffffff"
                    border_border="1px solid #ffffff"
                    border_border_radius="30px"
                    padding="6px 17px"
                    className="mt-[20px] w-auto text-[16px] sm:text-[20px] 2xl:text-[24px] leading-[20px] sm:leading-[25px] 2xl:leading-[31px] py-[6px] px-[17px] sm:py-[9px] sm:px-[25px] 2xl:py-[12px] 2xl:px-[34px] "
                  />
                </Link>
              </div>
            </div>

            {/* Image Container */}
            <div className="image-container-fix lg:w-[42%] w-full">
              <img
                src="/images/Bottom.webp"
                alt="Bio Gas Energy Background"
                className="rounded-[20px] md:rounded-[30px]"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesSection;
